<template>
  <div style="padding:8px 4px;">
    <!-- Video ID filter (server-side) -->
    <div class="yt-claims__filter">
      <i class="el-icon-search yt-claims__filter-icon"></i>
      <input
        v-model="videoIdInput"
        type="text"
        placeholder="동영상 ID로 검색"
        class="yt-claims__filter-input"
        @input="onInput"
        @keyup.enter="emitSearch(videoIdInput)"
      />
      <i v-if="loading" class="el-icon-loading yt-claims__filter-spinner"></i>
      <button
        v-if="videoIdInput"
        type="button"
        class="yt-claims__filter-clear"
        @click="clearFilter"
        aria-label="지우기"
      >
        <i class="el-icon-close"></i>
      </button>
    </div>

    <div v-if="loading && !items.length" style="padding:32px 0;text-align:center;opacity:0.4;font-size:14px;">불러오는 중…</div>
    <div v-else-if="!items.length" style="padding:32px 0;text-align:center;opacity:0.4;font-size:14px;">
      {{ videoIdFilter ? '일치하는 동영상이 없습니다.' : '소유권 주장이 없습니다.' }}
    </div>
    <template v-else>
      <table class="w-full" style="font-size:13px;border-collapse:collapse;">
        <thead>
          <tr style="border-bottom:2px solid rgba(0,0,0,0.08);">
            <th style="opacity:0.5;font-weight:600;padding:8px;text-align:left;">소유권 주장 ID</th>
            <th style="opacity:0.5;font-weight:600;padding:8px;text-align:left;">동영상 ID</th>
            <th style="opacity:0.5;font-weight:600;padding:8px;text-align:left;">콘텐츠 유형</th>
            <th style="opacity:0.5;font-weight:600;padding:8px;text-align:left;">상태</th>
            <th style="opacity:0.5;font-weight:600;padding:8px;text-align:left;">생성일</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="c in items"
            :key="c.id"
            style="border-bottom:1px solid rgba(0,0,0,0.05);cursor:pointer;"
            @click="open(c)"
          >
            <td style="padding:12px 8px;"><IdMono :value="c.id" :copyable="false" :truncate="22"></IdMono></td>
            <td style="padding:12px 8px;"><IdMono :value="c.videoId" :copyable="false"></IdMono></td>
            <td style="padding:12px 8px;opacity:0.5;">{{ c.contentType || '—' }}</td>
            <td style="padding:12px 8px;"><StatusPill :variant="statusVariant(c.status)"></StatusPill></td>
            <td style="padding:12px 8px;opacity:0.5;font-family:monospace;font-size:12px;">{{ formatDate(c.timeCreated) }}</td>
          </tr>
        </tbody>
      </table>
      <div style="display:flex;align-items:center;justify-content:space-between;padding:12px 8px;font-size:12px;opacity:0.6;">
        <span v-if="videoIdFilter">
          {{ items.length }}건 (검색 결과)
        </span>
        <span v-else>
          {{ items.length }}{{ total != null ? ` / ${total}` : '' }}건 표시
        </span>
        <el-button
          v-if="hasMore"
          size="mini"
          :loading="loadingMore"
          @click="$emit('load-more')"
        >더 불러오기</el-button>
      </div>
    </template>
  </div>
</template>

<script>
import IdMono from '../components/IdMono.vue'
import StatusPill from '../components/StatusPill.vue'
import { toLocalDate } from '../dateFormat.js'

const STATUS_MAP = {
  active: 'active',
  inactive: 'inactive',
  pending: 'pending',
  takedown: 'takedown',
  appealed: 'appealed',
  disputed: 'disputed',
}

export default {
  name: 'AssetClaimsTab',
  components: { IdMono, StatusPill },
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    loadingMore: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: false },
    total: { type: Number, default: null },
    videoIdFilter: { type: String, default: '' },
  },
  data() {
    return { videoIdInput: this.videoIdFilter || '' }
  },
  watch: {
    videoIdFilter(val) {
      if ((val || '') !== this.videoIdInput) this.videoIdInput = val || ''
    },
  },
  methods: {
    open(c) { this.$router.push(`/youtube-cid/claim/${c.id}`) },
    statusVariant(s) { return STATUS_MAP[(s || '').toLowerCase()] || 'inactive' },
    formatDate(t) {
      if (!t) return '—'
      return toLocalDate(t) || t
    },
    onInput() {
      clearTimeout(this._debounce)
      this._debounce = setTimeout(() => this.emitSearch(this.videoIdInput), 350)
    },
    emitSearch(val) {
      clearTimeout(this._debounce)
      const trimmed = (val || '').trim()
      if (trimmed === (this.videoIdFilter || '')) return
      this.$emit('update:videoIdFilter', trimmed)
    },
    clearFilter() {
      this.videoIdInput = ''
      this.emitSearch('')
    },
  },
  beforeDestroy() {
    clearTimeout(this._debounce)
  },
}
</script>

<style lang="scss" scoped>
.yt-claims__filter {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  margin: 4px 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: #fff;
  transition: border-color 140ms ease;

  &:focus-within { border-color: rgba(0, 0, 0, 0.26); }
}

.yt-claims__filter-icon {
  font-size: 14px;
  color: rgba(15, 15, 15, 0.42);
  flex-shrink: 0;
}

.yt-claims__filter-input {
  flex: 1;
  height: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 13px;
  color: #0f0f0f;
  font-family: inherit;

  &::placeholder { color: rgba(15, 15, 15, 0.42); }
}

.yt-claims__filter-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: rgba(15, 15, 15, 0.56);
  cursor: pointer;
  transition: background-color 120ms ease;

  &:hover { background: rgba(15, 15, 15, 0.06); color: #0f0f0f; }
  i { font-size: 12px; }
}

.yt-claims__filter-spinner {
  font-size: 14px;
  color: rgba(15, 15, 15, 0.42);
  margin-right: 4px;
}
</style>
